import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useStripe } from '../contexts/StripeContext';
import CreditsDisplay from './CreditsDisplay';

const Navigation: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout, loading } = useAuth();
  const { } = useStripe(); // We still need the context for CreditsDisplay
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  
  const handleLogout = async () => {
    if (isLoggingOut) return; // Prevent multiple clicks
    
    setIsLoggingOut(true);
    try {
      console.log('Logging out...');
      await logout();
      console.log('Logout successful, redirecting to login page');
      navigate('/login');
    } catch (error) {
      console.error('Logout failed:', error);
    } finally {
      setIsLoggingOut(false);
    }
    // Close mobile menu after logout
    setMobileMenuOpen(false);
  };
  
  // Close menu when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setMobileMenuOpen(false);
      }
    }
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);
  
  // Close mobile menu when route changes
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);
  
  return (
    <nav className="nav-container">
      <div className="nav-content">
        <div className="flex items-center">
          <Link to="/" className="flex items-center">
            <img src="/DTM-logo.png" alt="Deep Thought Matrix Logo" className="h-8 w-auto mr-2" />
            <span className="nav-brand">Research Agent</span>
          </Link>
        </div>
        
        {/* Mobile menu button */}
        <button 
          className="block md:hidden hamburger-button"
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          aria-label="Toggle menu"
        >
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" className="w-6 h-6">
            {mobileMenuOpen ? (
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            ) : (
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
            )}
          </svg>
        </button>
        
        {/* Desktop navigation */}
        <div className="hidden md:flex nav-links">
          {user && (
            <>
              <Link 
                to="/" 
                className={`nav-link ${location.pathname === '/' ? 'active' : ''}`}
              >
                New Research
              </Link>
              <Link 
                to="/reports" 
                className={`nav-link ${location.pathname === '/reports' ? 'active' : ''}`}
              >
                Reports
              </Link>
              <Link 
                to="/payment" 
                className={`nav-link ${location.pathname === '/payment' ? 'active' : ''}`}
              >
                Buy Credits
              </Link>
            </>
          )}
          
          {loading ? (
            <span className="nav-link opacity-50">Loading...</span>
          ) : user ? (
            <div className="flex items-center">
              <CreditsDisplay className="mr-4" />
              <span className="text-sm text-gray-600 mr-2">
                {user.displayName || user.email}
              </span>
              <button 
                onClick={handleLogout}
                disabled={isLoggingOut}
                className={`nav-link ${isLoggingOut ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                {isLoggingOut ? 'Logging out...' : 'Logout'}
              </button>
            </div>
          ) : (
            <Link 
              to="/login" 
              className={`nav-link ${location.pathname === '/login' ? 'active' : ''}`}
            >
              Login
            </Link>
          )}
        </div>
      </div>
      
      {/* Mobile menu dropdown */}
      {mobileMenuOpen && (
        <div 
          ref={menuRef}
          className="md:hidden mobile-menu-dropdown"
        >
          {user ? (
            <div className="flex flex-col">
              <Link 
                to="/" 
                className={`mobile-nav-link ${location.pathname === '/' ? 'active' : ''}`}
              >
                New Research
              </Link>
              <Link 
                to="/reports" 
                className={`mobile-nav-link ${location.pathname === '/reports' ? 'active' : ''}`}
              >
                Reports
              </Link>
              <Link 
                to="/payment" 
                className={`mobile-nav-link ${location.pathname === '/payment' ? 'active' : ''}`}
              >
                Buy Credits
              </Link>
              <div className="mobile-nav-divider"></div>
              <div className="px-4 py-2">
                <CreditsDisplay />
              </div>
              <div className="px-4 py-2 text-sm text-gray-300">
                {user.displayName || user.email}
              </div>
              <button 
                onClick={handleLogout}
                disabled={isLoggingOut}
                className={`mobile-nav-link ${isLoggingOut ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                {isLoggingOut ? 'Logging out...' : 'Logout'}
              </button>
            </div>
          ) : (
            <Link 
              to="/login" 
              className={`mobile-nav-link ${location.pathname === '/login' ? 'active' : ''}`}
            >
              Login
            </Link>
          )}
        </div>
      )}
    </nav>
  );
};

export default Navigation;
